import React, { useState, useRef, useEffect } from 'react';

const DOT_STYLE = {
  width: 8, height: 8, borderRadius: '50%',
  backgroundColor: '#94a3b8', display: 'inline-block'
};

const msgBoasVindas = (nome) =>
  `Olá! Sou ${nome}, seu assistente virtual. 🤖\n\nPosso te ajudar a:\n• Ver vagas disponíveis\n• Fazer ou cancelar agendamentos\n• Tirar dúvidas sobre horários\n\nComo posso te ajudar hoje?`;

export const ChatBot = ({ createApi }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [nomeBot, setNomeBot] = useState('Alma');
  const [botAtivo, setBotAtivo] = useState(true);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Carrega nome e status do bot na montagem
  useEffect(() => {
    fetch('/api/bot/info')
      .then(r => r.json())
      .then(data => {
        const nome = data.nome_bot || 'Alma';
        setNomeBot(nome);
        setBotAtivo(data.ativo !== false);
        setMessages([{ role: 'assistant', text: msgBoasVindas(nome) }]);
      })
      .catch(() => {
        setMessages([{ role: 'assistant', text: msgBoasVindas('Alma') }]);
      });
  }, []);

  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [messages, isOpen]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', text }]);
    setLoading(true);

    try {
      const api = createApi();
      const response = await api.post('/chat', { message: text, conversationHistory: history });
      setMessages(prev => [...prev, { role: 'assistant', text: response.data.reply }]);
      setHistory(response.data.history);
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: 'Desculpe, ocorreu um erro. Por favor, tente novamente ou entre em contato com a administração.'
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([{ role: 'assistant', text: msgBoasVindas(nomeBot) }]);
    setHistory([]);
  };

  // Bot desativado no painel → não exibe nada
  if (!botAtivo) return null;

  return (
    <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999 }}>
      {/* Chat Window */}
      {isOpen && (
        <div style={{
          position: 'absolute',
          bottom: 72,
          right: 0,
          width: 360,
          maxHeight: 520,
          backgroundColor: 'white',
          borderRadius: 16,
          boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          border: '1px solid #e2e8f0',
          animation: 'fadeIn 0.2s ease'
        }}>
          {/* Header */}
          <div style={{
            background: 'linear-gradient(135deg, var(--admin-header-inicio, #19981b) 0%, var(--admin-header-fim, #106311) 100%)',
            color: 'white',
            padding: '14px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 10
          }}>
            <div style={{
              width: 38,
              height: 38,
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 20,
              flexShrink: 0
            }}>
              🤸‍♀️
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{nomeBot}</div>
              <div style={{ fontSize: '0.72rem', opacity: 0.85 }}>Assistente virtual • Online</div>
            </div>
            <button
              onClick={clearChat}
              title="Nova conversa"
              style={{
                background: 'rgba(255,255,255,0.15)',
                border: 'none',
                color: 'white',
                borderRadius: 8,
                cursor: 'pointer',
                padding: '4px 8px',
                fontSize: '0.75rem'
              }}
            >
              Nova ↺
            </button>
          </div>

          {/* Messages */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '12px 12px 4px',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            maxHeight: 340,
            scrollbarWidth: 'thin'
          }}>
            {messages.map((msg, i) => (
              <div key={i} style={{
                display: 'flex',
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start'
              }}>
                <div style={{
                  maxWidth: '82%',
                  padding: '9px 13px',
                  borderRadius: msg.role === 'user'
                    ? '16px 16px 4px 16px'
                    : '16px 16px 16px 4px',
                  backgroundColor: msg.role === 'user' ? 'var(--admin-header-inicio, #19981b)' : '#f1f5f9',
                  color: msg.role === 'user' ? 'white' : '#1e293b',
                  fontSize: '0.865rem',
                  lineHeight: 1.55,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {msg.text}
                </div>
              </div>
            ))}

            {loading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{
                  padding: '10px 14px',
                  borderRadius: '16px 16px 16px 4px',
                  backgroundColor: '#f1f5f9',
                  display: 'flex',
                  gap: 5,
                  alignItems: 'center'
                }}>
                  <span style={{ ...DOT_STYLE, animation: 'chatPulse 1.2s infinite 0s' }} />
                  <span style={{ ...DOT_STYLE, animation: 'chatPulse 1.2s infinite 0.2s' }} />
                  <span style={{ ...DOT_STYLE, animation: 'chatPulse 1.2s infinite 0.4s' }} />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div style={{
            padding: '10px 12px',
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            gap: 8,
            alignItems: 'flex-end'
          }}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Envie uma mensagem..."
              disabled={loading}
              rows={1}
              style={{
                flex: 1,
                padding: '9px 13px',
                borderRadius: 20,
                border: '1px solid #e2e8f0',
                fontSize: '0.875rem',
                outline: 'none',
                resize: 'none',
                maxHeight: 80,
                overflowY: 'auto',
                fontFamily: 'inherit',
                backgroundColor: loading ? '#f8fafc' : 'white',
                lineHeight: 1.4,
                scrollbarWidth: 'none'
              }}
            />
            <button
              onClick={sendMessage}
              disabled={loading || !input.trim()}
              style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: input.trim() && !loading ? 'var(--admin-header-inicio, #19981b)' : '#e2e8f0',
                color: input.trim() && !loading ? 'white' : '#94a3b8',
                border: 'none',
                cursor: input.trim() && !loading ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                transition: 'all 0.2s ease',
                fontSize: 17
              }}
            >
              ▶
            </button>
          </div>
        </div>
      )}

      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(o => !o)}
        style={{
          width: 58,
          height: 58,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, var(--admin-header-inicio, #19981b) 0%, var(--admin-header-fim, #106311) 100%)',
          color: 'white',
          border: 'none',
          cursor: 'pointer',
          fontSize: isOpen ? 22 : 28,
          boxShadow: '0 4px 20px rgba(25, 152, 27, 0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.3s ease',
          position: 'relative'
        }}
        title={isOpen ? 'Fechar chat' : 'Abrir assistente'}
      >
        {isOpen ? '✕' : '💬'}
        {!isOpen && messages.length === 1 && (
          <span style={{
            position: 'absolute',
            top: -2,
            right: -2,
            width: 14,
            height: 14,
            borderRadius: '50%',
            backgroundColor: '#22c55e',
            border: '2px solid white'
          }} />
        )}
      </button>

      <style>{`
        @keyframes chatPulse {
          0%, 80%, 100% { opacity: 0.3; transform: scale(0.85); }
          40% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};
